import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { buttonVariants } from "./button-variants";
import {
  GradientBorderButton,
  GradientBorderButtonProps,
} from "./GradientBorderButton";
import { IconLoader2 } from "@tabler/icons-react";

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "leading" | "trailing";
  variant?: Exclude<
    VariantProps<typeof buttonVariants>["variant"],
    "gradientBorder" | "gradientBorderSubtle"
  >;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps | GradientBorderButtonProps
>((props, ref) => {
  if (
    props.variant === "gradientBorder" ||
    props.variant === "gradientBorderSubtle"
  ) {
    return <GradientBorderButton {...props} ref={ref} />;
  }

  const {
    className,
    variant,
    size,
    children,
    icon,
    iconPosition = "trailing",
    asChild = false,
    isLoading = false,
    ...restProps
  } = props as ButtonProps;

  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          className: cn(
            {
              "pl-4": icon && iconPosition === "leading",
              "pr-4": icon && iconPosition === "trailing",
            },
            className,
          ),
        }),
      )}
      ref={ref}
      {...restProps}
    >
      {asChild ? (
        children
      ) : (
        <>
          <div
            className={cn(isLoading && "opacity-0", {
              "flex items-center gap-2.5": icon,
            })}
          >
            {iconPosition === "leading" && icon}
            {children}
            {iconPosition === "trailing" && icon}
          </div>
          {isLoading && (
            <IconLoader2 className="absolute top-1/2 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2 animate-spin" />
          )}
        </>
      )}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button };
